import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, X, PackageCheck, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * SingleFulfilmentModal — Phase 5.2 `process_owner_review` + `accounts_review`.
 *
 * Single-actor mark_fulfilled UX (no twin gate). Used at:
 *   • process_owner_review — Shivmohan.V (Transport PO) marks fulfilled; the
 *     ticket advances to accounts_review.
 *   • accounts_review — first of Amey.G OR Sangita.K to mark fulfilled closes
 *     the requisition. One-of gate; either party's action completes the flow.
 *
 * Rules:
 *   • Remarks optional (fulfilment log, not a decision).
 *   • Single "Mark Fulfilled" button.
 *   • Yellow "acting as" banner shown when a super_admin acts outside their
 *     normal queue (universal Phase 5.2 super-admin bypass).
 */
export default function SingleFulfilmentModal({
  open, onClose, onDone, requisition, currentUser, stageLabel, closesOnMark,
}) {
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setError("");
  }, [open, requisition?.ref_code]);

  if (!open || !requisition) return null;

  const isSuper = currentUser?.role === "super_admin";
  const inQueue = (requisition.current_approvers || []).includes(currentUser?.id);
  const superBypassing = isSuper && !inQueue;

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/requisitions/${requisition.ref_code}/action`,
        { action: "mark_fulfilled", remarks: remarks.trim() },
      );
      const closed = data?.status === "completed";
      toast.success(closed
        ? `Fulfilled — requisition ${requisition.ref_code} is now Completed.`
        : `Marked fulfilled — advanced to next stage.`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="single-fulfilment-modal"
    >
      <div
        className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <PackageCheck className="text-[#2d6b6b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Mark Fulfilled — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {superBypassing && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] px-3 py-2 rounded flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>
                You are acting as <strong>{stageLabel || requisition.current_stage}</strong>
                {" "}on behalf of the assigned approver. This action will be logged
                as a super-admin bypass in the audit trail.
              </div>
            </div>
          )}

          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#2d6b6b] px-3 py-2 rounded">
            {closesOnMark
              ? <>Marking fulfilled here will <strong>close</strong> the requisition
                  (Accounts stage — either Amey.G or Sangita.K may finalise).</>
              : <>Marking fulfilled here will advance the requisition to the
                  Accounts stage.</>}
          </div>

          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">
              {error}
            </div>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks
              <span className="ml-2 text-[#6e7770] text-xs font-normal">(optional)</span>
            </span>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
              rows={3}
              placeholder="Optional note for the fulfilment log…"
              className="input-vgws min-h-[80px]"
              data-testid="single-fulfilment-remarks"
            />
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="single-fulfilment-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a] ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="single-fulfilment-confirm"
          >
            {busy
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <CheckCircle2 size={16} />}
            {busy ? "Marking…" : "Mark Fulfilled"}
          </button>
        </div>
      </div>
    </div>
  );
}
